import { useEffect, useMemo } from 'react'
import { doc, type DocumentReference } from 'firebase/firestore'
import type { EmailCategory, EventMilestonesSeen } from '@shared/email'
import { db } from '../lib/firebase'
import { setDoc } from '../lib/firestore'
import { log } from '../lib/logger'
import { useFirestoreDoc } from './useFirestore'

/**
 * Records, once per event and milestone, that the user saw it in the app — the
 * lineup, the published schedule, the cancelled notice — so the milestone email
 * (sendMilestoneEmails) skips them. `showing` names what is on screen right now;
 * pass `null` uid for guests, who get no email.
 */
export function useEventMilestonesSeen(
  uid: string | null,
  eventId: string | undefined,
  showing: Partial<Record<EmailCategory, boolean>>,
) {
  const ref = useMemo(
    () => (uid ? (doc(db, 'users', uid, 'private', 'eventMilestonesSeen') as DocumentReference<EventMilestonesSeen>) : null),
    [uid],
  )
  const { data, loading } = useFirestoreDoc<EventMilestonesSeen>(ref)
  const { lineup, schedule, cancelled } = showing

  useEffect(() => {
    if (!ref || !eventId || loading) return
    const now = Date.now()
    const shown = { lineup, schedule, cancelled }
    const missing = (Object.keys(shown) as EmailCategory[]).filter(c => shown[c] && !data?.[c]?.[eventId])
    if (missing.length === 0) return
    const update = Object.fromEntries(missing.map(c => [c, { [eventId]: now }]))
    void setDoc(ref, update, { merge: true }).catch(() => {
      // Worst case they get an email about something they saw — not worth surfacing.
      log('milestones', 'failed to write eventMilestonesSeen')
    })
  }, [ref, eventId, loading, data, lineup, schedule, cancelled])
}
